/**
 * ErrorBoundary — catches any render error thrown by children so the whole
 * app doesn't go white. Shows a friendly, copy-able error card the user (or
 * support) can read.
 *
 * Usage:
 *   <ErrorBoundary label="Email import">
 *     <ImportDialog />
 *   </ErrorBoundary>
 */
import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface the error in the console so power users can copy it back to us.
    console.error(`[ErrorBoundary:${this.props.label || "unknown"}]`, error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div
        role="alert"
        data-testid={`error-boundary-${(this.props.label || "root").toLowerCase().replace(/\s+/g, "-")}`}
        style={{
          margin: 16,
          padding: 16,
          borderRadius: 12,
          background: "#FDECEA",
          border: "1px solid #E9B4AA",
          color: "#7A2917",
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          Something went wrong{this.props.label ? ` in ${this.props.label}` : ""}.
        </div>
        <div style={{ fontFamily: "'JetBrains Mono','Menlo',monospace", fontSize: 12, whiteSpace: "pre-wrap", marginBottom: 10 }}>
          {msg}
        </div>
        <button
          type="button"
          onClick={this.reset}
          style={{
            background: "#0F3B2E", color: "white", border: "none",
            padding: "6px 12px", borderRadius: 8,
            fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
